import React from 'react';

import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './AboutPage.module.css';

const SUMMARY_STEPS = [
  {
    icon: '🔍',
    title: 'Find a kit you love',
    body: 'Browse curated party kits in your city, designed by talented hosts.',
  },
  {
    icon: '📅',
    title: 'Book your dates',
    body: 'Pick your event date and arrange pickup or delivery with the host.',
  },
  {
    icon: '🎉',
    title: 'Host beautifully',
    body: 'Use the kit, take the photos, return it. We handle the rest.',
  },
];

const RENTER_STEPS = [
  {
    title: 'Browse curated kits',
    body:
      'Every kit is hand-selected for design, quality, and condition. Browse by occasion or search by needed services.',
  },
  {
    title: 'Pick your dates',
    body:
      'Choose your event date, then choose pickup directly from the host or delivery to your venue.',
  },
  {
    title: 'Connect with your host',
    body:
      'Message your host directly with styling preferences, special requests, or delivery timing. They typically respond within hours.',
  },
  {
    title: 'Host your party',
    body:
      'Use the kit. Take the photos. Enjoy not having to figure out where to store vintage chargers afterward.',
  },
  {
    title: 'Return it',
    body:
      'Return the kit by the agreed-upon time and in good condition. Your host takes care of the rest.',
    fullWidth: true,
  },
];

const HOST_STEPS = [
  {
    title: 'Apply to list',
    body:
      "We review every kit and service for design and quality before it goes live. This isn't a barrier, it's the reason renters trust us.",
  },
  {
    title: 'Set your terms',
    body:
      "You decide what your kit includes, what it costs, your delivery radius, and what dates you're available.",
  },
  {
    title: 'Get booked',
    body:
      'Renters discover your kit, message you, and book directly. We handle payments, deposits, and customer support.',
  },
  {
    title: 'Earn',
    body:
      "Each booking is income on work you've already created. Some hosts list as a side income, others have built it into something bigger.",
  },
];

const FAQS = [
  {
    q: 'Is Merrily like Airbnb but for parties?',
    a: "Sort of, but with more curation. We don't list everything, we curate. Every kit is reviewed before it goes live, and we work closely with hosts to make sure what shows up at your event matches what you saw online.",
  },
  {
    q: 'What happens if something gets damaged?',
    a: 'Normal wear from party use is never charged. For accidents, the refundable security deposit (half the rental, between $20 and $500) is the first line of protection, and if accidental damage goes beyond it, Merrily’s damage protection covers the gap, up to 50% of the item’s replacement cost.',
  },
  {
    q: 'What cities do you serve?',
    a: "We're launching in Santa Barbara first, then expanding through California and beyond. If we're not in your city yet, you can sign up to be notified when we are.",
  },
  {
    q: 'Can I rent and host?',
    a: 'Absolutely. Many of our hosts also rent from other hosts. Your dashboard switches between modes seamlessly.',
  },
  {
    q: 'Can I book just the kit, or also things like catering and florals?',
    a: "Both. Every kit can be booked on its own, but many hosts also offer add-ons like florals, balloons, catering, or full setup. You'll see what each host offers on their listing page.",
  },
];

const AboutPage = () => {
  return (
    <Page
      title="About"
      description="Merrily is a marketplace for renting beautifully styled party kits from local hosts. Throw the party, skip the closet full of decor."
      className={css.root}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <section className={css.hero}>
          <div className={css.container}>
            <p className={css.pageEyebrow}>About Merrily</p>
            <h1 className={css.heroTitle}>A new way to host</h1>
            <p className={css.heroLead}>
              Throwing a beautiful party shouldn't mean buying a closet full of decor,
              Pinterest-spiraling at 2am, or hiring a full event planner. There's an easier way.
            </p>
          </div>
        </section>

        <div className={css.containerWide}>
          <div className={css.summaryCard}>
            <p className={css.summaryEyebrow}>How Merrily works in 30 seconds</p>
            <div className={css.summaryGrid}>
              {SUMMARY_STEPS.map(step => (
                <div key={step.title} className={css.summaryStep}>
                  <div className={css.summaryIcon} aria-hidden="true">
                    {step.icon}
                  </div>
                  <h4 className={css.summaryStepTitle}>{step.title}</h4>
                  <p className={css.summaryStepBody}>{step.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <section className={`${css.section} ${css.sectionShaded}`}>
          <div className={css.container}>
            <h2 className={`${css.h2} ${css.centered}`}>Why we built it</h2>

            <p className={css.bodyText}>
              The stress of designing and planning an event shouldn't be why you don't get together
              with your loved ones. Most of us want to host more, like birthday dinners, baby
              showers, casual Sunday gatherings, but the effort of pulling it off well is what gets
              in the way. Buying decor you don't need. Storing it. Throwing half of it out a year
              later. Doing it all again next time.
            </p>

            <div className={css.pullQuoteAccent}>
              <p className={css.quoteText}>
                More parties, less buying, less stress, more togetherness.
              </p>
            </div>

            <p className={css.bodyText}>
              Merrily is built around a simple idea: Rent something beautiful, use it, return it.
              Skip the closet full of things you'll never use again.
            </p>
          </div>
        </section>

        <section className={css.section}>
          <div className={css.containerWide}>
            <div className={css.sectionIntro}>
              <h2 className={`${css.h2} ${css.centered}`}>For renters</h2>
              <p className={css.sectionIntroBody}>
                Five steps from "I should throw a party" to "I just hosted a beautiful one."
              </p>
            </div>

            <div className={css.stepsGrid}>
              {RENTER_STEPS.map((step, idx) => (
                <div
                  key={step.title}
                  className={`${css.stepCard} ${step.fullWidth ? css.stepCardFullWidth : ''}`}
                >
                  <div className={css.stepNumber}>{idx + 1}</div>
                  <h3 className={css.stepTitle}>{step.title}</h3>
                  <p className={css.stepBody}>{step.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className={`${css.section} ${css.sectionShaded}`}>
          <div className={css.containerWide}>
            <div className={css.sectionIntro}>
              <h2 className={`${css.h2} ${css.centered}`}>For hosts</h2>
            </div>

            <div className={css.hostIntro}>
              <p>
                If you have beautifully styled kits, whether you're a professional event designer,
                a stylist, or someone who just makes really thoughtful celebrations, Merrily is a
                way to turn that work into ongoing income.
              </p>
            </div>

            <div className={`${css.stepsGrid} ${css.stepsGridHost}`}>
              {HOST_STEPS.map((step, idx) => (
                <div key={step.title} className={css.stepCard}>
                  <div className={css.stepNumber}>{idx + 1}</div>
                  <h3 className={css.stepTitle}>{step.title}</h3>
                  <p className={css.stepBody}>{step.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className={css.faqSection}>
          <div className={css.container}>
            <h2 className={`${css.h2} ${css.centered}`}>Common questions</h2>

            {FAQS.map(item => (
              <div key={item.q} className={css.faqItem}>
                <p className={css.faqQuestion}>{item.q}</p>
                <p className={css.faqAnswer}>{item.a}</p>
              </div>
            ))}
          </div>
        </section>
      </LayoutSingleColumn>
    </Page>
  );
};

export default AboutPage;
